import { InferGetStaticPropsType, GetStaticProps } from 'next';
import { NextSeo, ArticleJsonLd } from 'next-seo';
import RevalidateSettings from 'src/lib/revalidate_settings';
import { AppRoutes } from 'src/lib/routes';
import BlogLayout from 'src/components/BlogLayout';
import { Blog, Country, TravelItinerary } from 'src/types';
import ApiClient from 'src/lib/api_client';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import dayjs from 'dayjs';
import { DBDateFormat } from 'src/lib/constants';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

const MagazineIndex: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  featuredBlog,
  mostReadBlogs,
  travelItineraries,
  countries,
  blogsCount
}) => {
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/magazine/index')
  return (
    <>
      <NextSeo
        title={translation('metaTitle')}
        description={translation('metaDescription')}
        canonical={addLocaleToUrl(AppRoutes.magazinesRoute({}).toUrl())}
        openGraph={{
          title: translation('metaTitle'),
          description: translation('metaDescription'),
          url: addLocaleToUrl(AppRoutes.magazinesRoute({}).toUrl()),
        }}
      />
      <WithDefaultJsonLd>
        <ArticleJsonLd
          title={translation('metaTitle')}
          authorName="Seek Sophie"
          url={addLocaleToUrl(AppRoutes.magazinesRoute({}).toUrl())}
          description={translation('metaDescription')}
          datePublished={dayjs().format(DBDateFormat)}
          images={[]}
        />
      </WithDefaultJsonLd>

      <BlogLayout
        countries={countries}
        featuredBlog={featuredBlog}
        mostReadBlogs={mostReadBlogs}
        travelItineraries={travelItineraries}
        blogsCount={blogsCount}
      />
    </>
  );
};

export const getStaticProps: GetStaticProps<{
  countries: Country[];
  featuredBlog: Blog | null;
  mostReadBlogs: Blog[];
  travelItineraries: TravelItinerary[];
  blogsCount: number;
}> = async (context) => {
  const translations = await prepareTranslations(context, ['pages/magazine/index']);
  const [{ data: featuredBlogs, pagination }] = await ApiClient.getBlogs({
    sort_by: ['featured', 'latest'],
    page: 1,
    per_page: 1,
  }, { 'X-NEXT-LOCALE': translations.locale });

  const [{ data: mostReadBlogs }] = await ApiClient.getBlogs({
    excluded_ids: featuredBlogs.map(o => o.id),
    sort_by: ['most_read'],
    page: 1,
    per_page: 4,
  }, { 'X-NEXT-LOCALE': translations.locale });

  const [{ data: travelItineraries }] = await ApiClient.getTravelItineraries({
    q: {
      status_eq: 'published',
      s: 'views_count desc',
    },
    page: 1,
    per_page: 20,
  }, { 'X-NEXT-LOCALE': translations.locale });

  const [{ data: countries }] = await ApiClient.getCountries({
    q: {
      published_at_not_null: 1,
      options_linked_with_blogs_existed_true: 1
    },
  }, { 'X-NEXT-LOCALE': translations.locale });

  return {
    props: {
      blogsCount: pagination.totalCount,
      countries: countries,
      featuredBlog: featuredBlogs[0] || null,
      mostReadBlogs: mostReadBlogs,
      travelItineraries: travelItineraries,
      ...translations
    },
    revalidate: RevalidateSettings.medium,
  };
};

export default MagazineIndex;
